import { IT_INVENTORY_MDM_STATE, IT_INVENTORY_PROFILE_COMPLIANCE } from '@/lib/constants/itInventory'
import { HARDWARE_ASSET_STATUSES, HARDWARE_ASSET_TYPES } from '@/lib/constants/hardwareAssets'
import { CLOUD_RESOURCE_DETAILS, CLOUD_RESOURCE_TYPES } from '@/lib/constants/cloudResources'

export const IT_INVENTORY = {
  TITLE: 'IT Inventory',
  SEARCH_PLACEHOLDER: 'Search by hostname or owner',
  TABS: {
    LAPTOPS: 'Laptops',
    MOBILES: 'Mobiles',
    SERVERS: 'Servers',
    CLOUD: 'Cloud',
    CODE: 'Code',
    HARDWARE: 'Hardware',
  },
  SUMMARY: {
    AGENTS_DEPLOYED: 'agents deployed',
    COVERED: 'Covered',
    NOT_COVERED: 'Not covered',
    VIEW_EMPLOYEES: 'View employees',
    COVERAGE: 'Coverage',
  },
  TABLE: {
    ENDPOINT: 'Endpoint',
    UPDATES: 'Updates',
    OS: 'OS',
    AGENT: 'Agent',
    MDM: 'MDM',
    ASSIGNED_TO: 'Assigned to',
    LAST_SEEN: 'Last seen',
    PROFILE_COMPLIANCE: 'Profile compliance',
    HARDWARE: 'Hardware',
    ACCOUNT: 'Account',
    ACCOUNT_ID: 'Account ID',
    PROVIDER: 'Provider',
    REGIONS: 'Regions',
    REGIONS_COUNT: (count: number) => `${count} regions`,
    REPOSITORY: 'Repository',
    SCM: 'SCM',
    PROJECT_KEY: 'Project key',
    CLONE_URL: 'Clone URL',
    ADDED: 'Added',
    ITEM: 'Item',
    TYPE: 'Type',
    SERIAL: 'Serial',
    LOCATION: 'Location',
    STATUS: 'Status',
    NOTES: 'Notes',
    EMPTY: '-',
    UNASSIGNED: 'Unassigned',
    STORAGE_VALUE: (totalGb: number) => `${totalGb} GB`,
    RESOURCE: 'Resource',
    OWNER: 'Owner',
    USER_DATA: 'User data',
    NEEDS_OWNER: 'Needs owner',
    YES: 'Yes',
    NO: 'No',
  },
  DRAWER: {
    HARDWARE: 'Hardware',
    SYSTEM_DETAILS: 'System details',
    ARCHITECTURE: 'Architecture',
    SERIAL_NUMBER: 'Serial number',
    EDR_AGENT: 'EDR agent',
    EDR_AGENT_VALUE: (agentName: string, version?: string) => (version ? `${agentName} · ${version}` : agentName),
    MODEL: 'Model',
    STORAGE: 'Storage',
    NEVER_SEEN: '-',
    QUICK_ACTIONS: 'Quick actions',
  },
  HARDWARE_TYPES: {
    [HARDWARE_ASSET_TYPES.MONITOR]: 'Monitor',
    [HARDWARE_ASSET_TYPES.PRINTER]: 'Printer',
    [HARDWARE_ASSET_TYPES.MOUSE]: 'Mouse',
    [HARDWARE_ASSET_TYPES.DOCK]: 'Dock',
    [HARDWARE_ASSET_TYPES.USB_DRIVE]: 'USB drive',
    [HARDWARE_ASSET_TYPES.SECURITY_KEY]: 'Security key',
    [HARDWARE_ASSET_TYPES.OTHER]: 'Other',
  },
  HARDWARE_STATUS: {
    [HARDWARE_ASSET_STATUSES.IN_USE]: 'In use',
    [HARDWARE_ASSET_STATUSES.IN_STOCK]: 'In stock',
    [HARDWARE_ASSET_STATUSES.RETIRED]: 'Retired',
  },
  HARDWARE_FORM: {
    NEW_TITLE: 'New hardware asset',
    NEW_ASSET: 'New asset',
    BULK_UPLOAD: 'Bulk upload',
    ITEM_PLACEHOLDER: 'e.g. Dell U2723QE 27"',
    SERIAL_PLACEHOLDER: 'e.g. CN-0T2K9J-1',
    LOCATION_PLACEHOLDER: 'e.g. Madrid office',
    NOTES_PLACEHOLDER: 'Anything worth recording about this item',
    SELECT_TYPE: 'Select a type',
    SEARCH_TYPES: 'Search types',
    NO_TYPE_FOUND: 'No type found',
    SELECT_STATUS: 'Select a status',
    SEARCH_STATUSES: 'Search statuses',
    NO_STATUS_FOUND: 'No status found',
    CREATE: 'Create',
    CANCEL: 'Cancel',
    INCOMPLETE: 'Item, type, serial number and location are required',
    CREATED: 'Hardware item added',
    UPDATE_ERROR: 'Could not save the change',
    DELETE_ERROR: 'Could not delete the hardware item',
    DELETE_SUCCESS: 'Hardware item deleted',
    DELETE_DIALOG: {
      TITLE: 'Delete hardware item',
      QUESTION: 'Are you sure you want to delete',
      CANCEL: 'Cancel',
      DELETE: 'Delete',
    },
    NO_NOTES: 'No notes',
  },
  HARDWARE_BULK_UPLOAD: {
    TITLE: 'Import hardware from CSV',
    DESCRIPTION:
      'Upload a CSV file to import hardware in bulk. Every row must be valid: if any row has errors, nothing is created and the errors are listed below.',
    UPLOAD_FILE: 'Upload CSV',
    UPLOADING: 'Uploading...',
    DOWNLOAD_SAMPLE: 'Download sample CSV',
    CANCEL: 'Cancel',
    SUCCESS: 'Hardware imported successfully',
    VALIDATION_TABLE_ROW: 'Row',
    VALIDATION_TABLE_FIELD: 'Field',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Received value',
    HEADER_ERROR_TITLE: 'Your CSV header has problems',
    HEADER_ERROR_DESCRIPTION:
      'Fix the header row in your file and upload it again. The accepted columns are listed below.',
    ACCEPTED_COLUMNS_REQUIRED: 'Required columns',
    ACCEPTED_COLUMNS_OPTIONAL: 'Optional columns',
    SUMMARY_LINE: '{totalRows} rows checked, {errorCount} errors found. Fix them in your CSV and upload it again.',
    ERROR_UNKNOWN_COLUMN: 'Unknown column: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Required column missing: {field}',
    ERROR_EMPTY_FILE: 'The file has a header but no hardware rows',
    ERROR_MALFORMED_ROW: 'The number of values in this row does not match the header',
    ERROR_REQUIRED: '{field} is required',
    ERROR_TOO_LONG: '{field} must be 255 characters or fewer',
    ERROR_INVALID_TYPE: 'Invalid type "{receivedValue}". Allowed values: {allowedValues}',
    ERROR_INVALID_STATUS: 'Invalid status "{receivedValue}". Allowed values: {allowedValues}',
    ERROR_USER_NOT_FOUND: 'User not found in this organization: "{receivedValue}"',
    ERROR_DUPLICATE_IN_FILE: 'Duplicate serial number in this file: "{receivedValue}"',
    ERROR_DUPLICATE_IN_DB: 'Hardware with this serial number already exists: "{receivedValue}"',
  },
  CLOUD_FILTERS: {
    FILTER_BY_TYPE: 'Filter by type',
    FILTER_BY_PROVIDER: 'Filter by provider',
    FILTER_BY_ACCOUNT: 'Filter by account',
    FILTER_BY_OWNER: 'Filter by owner',
    FILTER_BY_USER_DATA: 'Filter by user data',
  },
  CLOUD_DRAWER: {
    OWNERSHIP: 'Ownership',
    DESCRIPTION: 'Description',
    NO_DESCRIPTION: 'No description',
    CONTAINS_USER_DATA: 'Contains user data',
    CONTAINS_USER_DATA_INFO: 'Stores or processes customer or employee personal data',
    DETAILS: 'Details',
    RESOURCE_ID: 'Resource ID',
    SOURCE: 'Source',
    SUBTITLE: (type: string, service: string) => `${type} · ${service}`,
    LAST_READ: (when: string) => `Last read ${when}`,
    TAGS: 'Tags',
    NO_TAGS: 'No tags',
    UPDATE_ERROR: 'Could not save the change',
  },
  CLOUD_RESOURCE_TYPES: {
    [CLOUD_RESOURCE_TYPES.ACCOUNT]: 'Cloud account',
    [CLOUD_RESOURCE_TYPES.KUBERNETES]: 'Kubernetes cluster',
    [CLOUD_RESOURCE_TYPES.COMPUTE]: 'Compute instance',
    [CLOUD_RESOURCE_TYPES.BUCKET]: 'Storage bucket',
    [CLOUD_RESOURCE_TYPES.DATABASE]: 'Database',
    [CLOUD_RESOURCE_TYPES.FUNCTION]: 'Function',
    [CLOUD_RESOURCE_TYPES.LOAD_BALANCER]: 'Load balancer',
    [CLOUD_RESOURCE_TYPES.REGISTRY]: 'Container registry',
    [CLOUD_RESOURCE_TYPES.KEY]: 'Encryption key',
  },
  CLOUD_RESOURCE_DETAILS: {
    [CLOUD_RESOURCE_DETAILS.ENGINE]: 'Engine',
    [CLOUD_RESOURCE_DETAILS.KUBERNETES_VERSION]: 'Kubernetes version',
    [CLOUD_RESOURCE_DETAILS.RUNTIME]: 'Runtime',
    [CLOUD_RESOURCE_DETAILS.INSTANCE_TYPE]: 'Instance type',
    [CLOUD_RESOURCE_DETAILS.IMAGE]: 'Image',
    [CLOUD_RESOURCE_DETAILS.NODES]: 'Nodes',
    [CLOUD_RESOURCE_DETAILS.MEMORY_MB]: 'Memory (MB)',
    [CLOUD_RESOURCE_DETAILS.SIZE]: 'Size',
    [CLOUD_RESOURCE_DETAILS.IMAGES]: 'Images',
    [CLOUD_RESOURCE_DETAILS.KEY_SPEC]: 'Key spec',
    [CLOUD_RESOURCE_DETAILS.ENCRYPTION]: 'Encryption',
    [CLOUD_RESOURCE_DETAILS.ROTATION_DAYS]: 'Rotation (days)',
    [CLOUD_RESOURCE_DETAILS.VERSIONING]: 'Versioning',
    [CLOUD_RESOURCE_DETAILS.BACKUP_RETENTION_DAYS]: 'Backup retention (days)',
    [CLOUD_RESOURCE_DETAILS.VULNERABILITY_SCANNING]: 'Vulnerability scanning',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_ENDPOINT]: 'Public endpoint',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_ACCESS]: 'Public access',
    [CLOUD_RESOURCE_DETAILS.PUBLIC_IP]: 'Public IP',
    [CLOUD_RESOURCE_DETAILS.TLS_POLICY]: 'TLS policy',
    [CLOUD_RESOURCE_DETAILS.WAF]: 'WAF',
  },
  // The sentence a tooltip needs; MDM_STATE_SHORT is the same state as a drawer field value.
  MDM_STATE: {
    [IT_INVENTORY_MDM_STATE.ONLINE]: 'Enrolled in MDM, online',
    [IT_INVENTORY_MDM_STATE.OFFLINE]: 'Enrolled in MDM, offline',
    [IT_INVENTORY_MDM_STATE.UNMANAGED]: 'Not enrolled in MDM',
    [IT_INVENTORY_MDM_STATE.NO_AGENT]: 'Enrolled in MDM, but the MDM agent is not installed',
    [IT_INVENTORY_MDM_STATE.UNAVAILABLE]: 'MDM is not responding, so enrolment could not be checked',
  },
  MDM_STATE_SHORT: {
    [IT_INVENTORY_MDM_STATE.ONLINE]: 'Online',
    [IT_INVENTORY_MDM_STATE.OFFLINE]: 'Offline',
    [IT_INVENTORY_MDM_STATE.UNMANAGED]: 'Not enrolled',
    [IT_INVENTORY_MDM_STATE.NO_AGENT]: 'MDM agent not installed',
    [IT_INVENTORY_MDM_STATE.UNAVAILABLE]: 'MDM unavailable',
  },
  PROFILE_COMPLIANCE_STATE: {
    [IT_INVENTORY_PROFILE_COMPLIANCE.PASS]: 'Pass',
    [IT_INVENTORY_PROFILE_COMPLIANCE.FAIL]: 'Fail',
    [IT_INVENTORY_PROFILE_COMPLIANCE.NOT_APPLICABLE]: 'N/A',
  },
}
